import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getAllBuilds } from "../../redux-store/testPlanBuildReducers/testPlanBuildActions";

import { Box, Grid, Typography } from "@mui/material";
import CustomButton from "../Common/CustomButton";
import LoadingOverlay from "../Common/LoadingOverlay";
import BuildCard from "../Common/BuildCard";
import { Visibility } from "@mui/icons-material";

const TestPlanBuildsDrawer = ({ testPlanId }) => {
  const dispatch = useDispatch();
  const pageSize = 10;
  const [pageNumber, setPageNumber] = useState(1);
  const [testPlanBuildList, setTestPlanBuildList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const isLoading = useSelector((state) => state.testPlanBuild.isLoading);
  const [expandedId, setExpandedId] = useState("");
  const isInitial = useRef(true);

  const styles = {
    container: {
      p: 2,
      backgroundColor: 'var(--background-primary)',
      color: 'var(--text-primary)',
    },
    header: {
      mb: 2,
      backgroundColor: 'var(--background-primary)',
    },
    title: {
      marginLeft: "16px",
      color: 'var(--text-primary)',
      fontWeight: 500,
    },
    viewAllButton: {
      mr: 2,
      ml: "auto",
      '& .MuiSvgIcon-root': {
        color: 'var(--text-primary)',
      },
    },
    loadMoreContainer: {
      width: "100%",
      textAlign: "center",
      backgroundColor: 'var(--background-primary)',
    },
    loadMoreButton: {
      my: 2,
      '&:hover': {
        backgroundColor: 'var(--hover-bg)',
      },
    },
  };

  useEffect(() => {
    getTestPlanBuildList(pageNumber, pageSize);
  }, []);

  const getTestPlanBuildList = async (pageNumber, pageSize) => {
    const response = await dispatch(
      getAllBuilds({
        testPlanId: testPlanId,
        pageNumber: pageNumber,
        pageSize: pageSize,
      })
    );
    if (response?.payload) {
      setTestPlanBuildList((prevState) => ([
        ...prevState,
        ...response.payload?.test_plan_builds,
      ]));
      setTotalPages(response.payload?.total_pages);
    }
    isInitial.current = false;
  };

  const onLoadMore = () => {
    setPageNumber((prevState) => prevState + 1);
    getTestPlanBuildList(pageNumber + 1, pageSize);
  };

  const onExpand = (buildId) => {
    if (buildId === expandedId) {
      setExpandedId("");
    } else {
      setExpandedId(buildId);
    }
  };

  const onViewBuild = (id) => {
    window.open(
      `${window.location.origin}/test-plan-builds/${id}`,
      "_blank"
    );
  };

  if (isLoading && isInitial.current) {
    return (<LoadingOverlay isLoading={isLoading} sx={{ width: "450px" }} />);
  }

  const renderBuilds = () => {
    return testPlanBuildList.map((build) => (
      <BuildCard
        key={build.id}
        testPlanId={testPlanId}
        build={build}
        isExpanded={expandedId === build.id}
        onExpand={() => {
          onExpand(build.id);
        }}
        onViewBuild={onViewBuild}
        buildType="testPlan"
      />
    ));
  };

  return (
    <Box sx={styles.container}>
      <Grid container alignItems="center" sx={styles.header}>
        <Typography variant="h6" sx={styles.title}>
          All builds({testPlanBuildList.length})
        </Typography>
        <CustomButton
          sx={styles.viewAllButton}
          variant="outlined"
          color="primary"
          disabled={false}
          onClick={() => {
            window.open(
              `${window.location.origin}/test-plans/${testPlanId}/build/?page=1`,
              "_blank"
            );
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View all builds
        </CustomButton>
      </Grid>
      {renderBuilds()}
      <Box sx={styles.loadMoreContainer}>
        {pageNumber < totalPages && !isLoading && (
          <CustomButton sx={styles.loadMoreButton} onClick={onLoadMore}>
            Load more
          </CustomButton>
        )}
      </Box>
    </Box>
  );
};

export default TestPlanBuildsDrawer;
